// Thin fetch wrapper: same-origin cookies, CSRF header, idempotency keys, typed errors (§4b, §5c).
import { ANON, getCacheOwner, getOwnerEpoch } from './owner';
import { getIdentityGen, getSessionTag, isActingEnded, actingName, onSessionChanged, reshowFrozenDialog } from './impersonation';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function newKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/**
 * Thrown when a response arrives after the signed-in account changed (or the session ended) since the
 * request was dispatched (§11c owner fence). Its data is discarded: no caller's success path, cache write
 * or session change can run with another account's response. Callers' error handlers ignore it.
 */
export class StaleOwnerError extends Error {
  constructor() {
    super('The account changed while this request was in flight.');
    this.name = 'StaleOwnerError';
  }
}
export const isStaleOwner = (e: unknown): boolean => e instanceof StaleOwnerError;

type Opts = {
  idempotencyKey?: string;
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /**
   * Owner epoch the caller belongs to (lib/ownerScope.ts). When set, the request is refused BEFORE it is
   * sent if the signed-in account has changed since then, so deferred work (an autosave timer, the second
   * step of a multi-step action) started by account A can never be dispatched with account B's cookie.
   */
  epoch?: number;
  /** Session tag to send instead of the tab's current one (the SSE identity probe sends the hub's tag). */
  sessionTag?: string | null;
};

export const SESSION_TAG_HEADER = 'X-Session-Tag';
/** Paths a frozen tab may still call: Exit (`/auth/impersonate/end`) and logout. */
const authPath = (path: string) => path.startsWith('/api/auth/');

let onUnauthorized: (() => void) | null = null;
/** The session layer registers a handler so a 401 anywhere drops to the login screen. */
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

export async function api<T = unknown>(method: string, path: string, body?: unknown, opts: Opts = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers };
  if (method !== 'GET' && method !== 'HEAD') headers['X-Requested-With'] = 'workdesk';
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  const write = method !== 'GET' && method !== 'HEAD';
  // Central write guard (§6): once the session as a client ended, no write leaves this tab except Exit.
  if (write && isActingEnded() && !authPath(path)) {
    reshowFrozenDialog();
    throw new ApiError(0, 'impersonation_ended', `Session as ${actingName() ?? 'the client'} ended. Exit to continue.`, null);
  }
  // Stale-tab fence (§2h): the tag of the session this tab believes it has.
  const sentTag = opts.sessionTag !== undefined ? opts.sessionTag : getSessionTag();
  if (sentTag) headers[SESSION_TAG_HEADER] = sentTag;
  const gen = getIdentityGen();
  // Owner epoch at dispatch. Network failures are rethrown as-is (the pending queue stores them under the
  // owner recorded at creation); any HTTP answer that lands after the owner changed is discarded.
  // Requests made while signed out (branding, login, setup) carry no account data, so they survive the
  // sign-in itself; they still never trigger the 401 handler once the owner has changed.
  if (opts.epoch !== undefined && getOwnerEpoch() !== opts.epoch) throw new StaleOwnerError();
  const epoch = opts.epoch ?? getOwnerEpoch();
  const signedIn = opts.epoch !== undefined || getCacheOwner() !== ANON;
  const stale = () => signedIn && getOwnerEpoch() !== epoch;
  const res = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    credentials: 'same-origin',
    signal: opts.signal,
  });
  if (stale()) throw new StaleOwnerError();
  // This tab switched identity itself while the request was in flight: drop the answer, no reload.
  if (gen !== getIdentityGen()) throw new StaleOwnerError();
  const resTag = res.headers?.get?.(SESSION_TAG_HEADER) ?? null;
  if (sentTag && resTag && resTag !== sentTag && !authPath(path)) {
    onSessionChanged();
    throw new StaleOwnerError();
  }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (stale() || gen !== getIdentityGen()) throw new StaleOwnerError();
  let data: unknown = undefined;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const d = (data ?? {}) as { error?: string; message?: string };
    if (res.status === 409 && d.error === 'session_changed') onSessionChanged();
    if (res.status === 401 && getOwnerEpoch() === epoch && !path.startsWith('/api/auth/') && path !== '/api/me') onUnauthorized?.();
    throw new ApiError(res.status, d.error ?? `http_${res.status}`, d.message ?? friendly(res.status), data);
  }
  return data as T;
}

export type ProbeOutcome = 'changed' | 'unauthorized' | 'transient' | 'ok';
/**
 * One identity probe for the SSE hub (§2h): GET /api/me with the hub's tag. A different tag means the
 * session changed; 401 means it ended (freeze, never reload); a network error or 5xx is transient.
 */
export async function probeIdentity(tag: string | null): Promise<ProbeOutcome> {
  let res: Response;
  try {
    res = await fetch('/api/me', { headers: { Accept: 'application/json', ...(tag ? { [SESSION_TAG_HEADER]: tag } : {}) }, credentials: 'same-origin' });
  } catch {
    return 'transient';
  }
  if (res.status === 401) return 'unauthorized';
  if (res.status === 409) return 'changed';
  if (!res.ok) return 'transient';
  const resTag = res.headers.get(SESSION_TAG_HEADER);
  let bodyTag: string | null = null;
  try {
    bodyTag = ((await res.json()) as { sessionTag?: string } | null)?.sessionTag ?? null;
  } catch {
    /* no body */
  }
  const now = resTag ?? bodyTag;
  return tag && now && now !== tag ? 'changed' : 'ok';
}

function friendly(status: number): string {
  if (status === 0) return 'You seem to be offline.';
  if (status === 404) return 'Not found, or you no longer have access.';
  if (status === 409) return 'This was changed by someone else. Refresh and try again.';
  if (status === 429) return 'Too many requests. Try again shortly.';
  if (status >= 500) return 'Something went wrong on our side. Try again.';
  return 'Request failed.';
}

export const get = <T>(path: string, signal?: AbortSignal, opts?: Opts) => api<T>('GET', path, undefined, { ...opts, signal });
export const post = <T>(path: string, body?: unknown, opts?: Opts) => api<T>('POST', path, body ?? {}, opts);
export const patch = <T>(path: string, body: unknown, opts?: Opts) => api<T>('PATCH', path, body, opts);
export const del = <T>(path: string, opts?: Opts) => api<T>('DELETE', path, undefined, opts);

export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') u.set(k, String(v));
  const s = u.toString();
  return s ? `?${s}` : '';
}
